import pcoIconUrl from "./assets/brand/planning-center/icon.svg?url";

import type { WidgetAuthConfig, WidgetViewer } from "./theme";

interface WidgetAuthBarProps {
  auth: WidgetAuthConfig | null;
  viewer: WidgetViewer | null;
  signInUrl: string | null;
  onSignIn: (url: string) => void;
}

const PROVIDER_ICONS: Record<string, string> = {
  planning_center: pcoIconUrl,
};

function ProviderIcon({ provider }: { provider: string }) {
  const src = PROVIDER_ICONS[provider];
  if (!src) return null;
  return (
    <img className="gw-authbar-icon" src={src} alt="" aria-hidden="true" />
  );
}

/**
 * Persistent auth affordance shown at the top of the panel body.
 *
 * - Not authenticated: render a small "Sign in with <provider>" pill
 *   that delegates to the host loader (`widget:navigate`) and starts
 *   the post-OAuth resume poll in WidgetApp.
 * - Authenticated: show a discreet "Signed in as Name" badge with an
 *   avatar if one was returned by the provider.
 * - No provider enabled: render nothing.
 */
export function WidgetAuthBar(props: WidgetAuthBarProps) {
  const { auth, viewer, signInUrl, onSignIn } = props;
  if (!auth?.enabled) return null;

  const provider = firstEnabledProvider(auth);
  if (!provider) return null;

  if (viewer?.connected) {
    const name = viewer.name || viewer.email || provider.label;
    // Credential expired: prompt a reconnect so the user fixes it before
    // hitting a refused write tool. `valid` is optional for forward-compat;
    // only an explicit `false` triggers the expired state.
    if (viewer.valid === false && signInUrl) {
      return (
        <button
          type="button"
          className="gw-authbar-btn gw-authbar-expired"
          onClick={() => onSignIn(signInUrl)}
          title={`Your ${provider.label} session expired — reconnect to continue`}
          aria-label={`Reconnect to ${provider.label}`}
        >
          <ProviderIcon provider={provider.key} />
          <span className="gw-authbar-btn-label">Session expired — Reconnect</span>
        </button>
      );
    }
    return (
      <div className="gw-authbar-row gw-authbar-connected">
        {viewer.avatar_url ? (
          <img
            className="gw-authbar-avatar"
            src={viewer.avatar_url}
            alt=""
            aria-hidden="true"
          />
        ) : (
          <ProviderIcon provider={provider.key} />
        )}
        <span className="gw-authbar-text">
          Signed in as <strong>{name}</strong>
        </span>
      </div>
    );
  }

  if (!signInUrl) return null;

  return (
    <button
      type="button"
      className="gw-authbar-btn"
      onClick={() => onSignIn(signInUrl)}
      title={`Sign in with ${provider.label} for personalized help`}
      aria-label={`Sign in with ${provider.label}`}
    >
      <ProviderIcon provider={provider.key} />
      <span className="gw-authbar-btn-label">Sign in</span>
    </button>
  );
}

function firstEnabledProvider(auth: WidgetAuthConfig) {
  for (const [key, p] of Object.entries(auth.providers)) {
    if (p.enabled) return { key, ...p };
  }
  return null;
}
